import { useCallback, useEffect, useMemo, useState } from "react";

import {
  getAnnouncement,
  type Announcement,
} from "@/api";
import {
  type AnnouncementPageKey,
  useAnnouncementsStore,
} from "@/stores/modules/announcements";

export type UseAnnouncementsResult = {
  announcements: Announcement[];
  total: number;
  page: number;
  limit: number;
  loading: boolean;
  error: string | null;
  refresh: (nextPage?: number) => Promise<void>;
};

export function useAnnouncements(page: number, limit: number): UseAnnouncementsResult {
  const pageKey = `${page}:${limit}` as AnnouncementPageKey;
  const cachedPage = useAnnouncementsStore((state) => state.pages[pageKey]);
  const emptyPage = useMemo(
    () => ({
      announcements: [] as Announcement[],
      total: 0,
      page,
      limit,
      loading: false,
      error: null,
    }),
    [limit, page]
  );
  const pageCache = cachedPage ?? emptyPage;
  const refreshAnnouncements = useAnnouncementsStore(
    (state) => state.refreshAnnouncements
  );

  const refresh = useCallback(
    async (nextPage: number = page) => {
      await refreshAnnouncements({ page: nextPage, limit });
    },
    [limit, page, refreshAnnouncements]
  );

  useEffect(() => {
    void refresh(page);
  }, [page, refresh]);

  // Revalidate on window/tab refocus; the store's TTL guard makes this a no-op
  // unless the cached page is stale, so it only refetches when needed.
  useEffect(() => {
    const revalidate = () => {
      if (document.visibilityState === "visible") {
        void refresh(page);
      }
    };
    document.addEventListener("visibilitychange", revalidate);
    window.addEventListener("focus", revalidate);
    return () => {
      document.removeEventListener("visibilitychange", revalidate);
      window.removeEventListener("focus", revalidate);
    };
  }, [page, refresh]);

  return {
    announcements: pageCache.announcements,
    total: pageCache.total,
    page: pageCache.page,
    limit: pageCache.limit,
    loading: pageCache.loading,
    error: pageCache.error,
    refresh,
  };
}

export type UseAnnouncementResult = {
  announcement: Announcement | null;
  loading: boolean;
  error: string | null;
};

export function useAnnouncement(id: string | undefined): UseAnnouncementResult {
  const [announcement, setAnnouncement] = useState<Announcement | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const numericId = id ? Number(id) : NaN;
  const invalidId = !Number.isInteger(numericId) || numericId <= 0;

  useEffect(() => {
    if (invalidId) return;

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await getAnnouncement(numericId);
        if (cancelled) return;
        if (res.ok) {
          setAnnouncement(res.data);
        } else {
          setAnnouncement(null);
          setError(res.error.message);
        }
      } catch (error) {
        if (cancelled) return;
        setAnnouncement(null);
        setError(error instanceof Error ? error.message : "load_failed");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [invalidId, numericId]);

  return {
    announcement: invalidId ? null : announcement,
    loading: invalidId ? false : loading,
    error: invalidId ? "invalid_id" : error,
  };
}
